import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { getCurrentSelectedTenant } from "../utils";

// The Segment analytics.js snippet object: an array that buffers calls until
// the real analytics.js script loads and replaces the stubbed methods.
type SegmentAnalytics = unknown[] & {
  [key: string]: unknown;
  initialize?: unknown;
  initialized?: boolean;
  invoked?: boolean;
  methods?: string[];
  factory?: (method: string) => (...args: unknown[]) => unknown;
  load?: (key: string, options?: unknown) => void;
  _loadOptions?: unknown;
  _writeKey?: string;
  SNIPPET_VERSION?: string;
  page?: (properties?: object) => void;
  track?: (event: string, properties?: object) => void;
};

declare global {
  interface Window {
    analytics?: SegmentAnalytics;
  }
}

// Hook to integrate Segment analytics. Call this in the main App component to initialize.
// Call inside other components to get access to trackEvent function.
// The hook will only load Segment if VITE_SEGMENT_WRITE_KEY is set, and it will only load once.
export const useSegment = () => {
  const segmentWriteKey = import.meta.env.VITE_SEGMENT_WRITE_KEY;
  const analyticsRef = useRef<SegmentAnalytics | null>(null);

  // Insert Segment analytics script on mount
  useEffect(() => {
    if (!segmentWriteKey) {
      return;
    }

    // define the key where the global analytics object will be accessible
    // customers can safely set this to be something else if need be
    const globalAnalyticsKey = "analytics";

    // Create a queue, but don't obliterate an existing one! A fresh queue is a
    // bare array that gets decorated with the stub methods below; the assertion
    // is needed because TS array types can't carry a string index signature.
    const analytics: SegmentAnalytics = (window[globalAnalyticsKey] =
      window[globalAnalyticsKey] || ([] as unknown as SegmentAnalytics));

    // If the real analytics.js is already on the page, return.
    if (analytics.initialize) return;

    // If the snippet was invoked already show an error.
    if (analytics.invoked) {
      if (window.console && console.info) {
        console.info("Segment snippet included twice.");
      }
      return;
    }

    // Invoked flag, to make sure the snippet
    // is never invoked twice.
    analytics.invoked = true;

    // A list of the methods in Analytics.js to stub.
    const methods = [
      "trackSubmit",
      "trackClick",
      "trackLink",
      "trackForm",
      "pageview",
      "identify",
      "reset",
      "group",
      "track",
      "ready",
      "alias",
      "debug",
      "page",
      "screen",
      "once",
      "off",
      "on",
      "addSourceMiddleware",
      "addIntegrationMiddleware",
      "setAnonymousId",
      "addDestinationMiddleware",
      "register",
    ];
    analytics.methods = methods;

    // Define a factory to create stubs. These are placeholders
    // for methods in Analytics.js so that you never have to wait
    // for it to load to actually record data. The `method` is
    // stored as the first argument, so we can replay the data.
    const factory = (e: string) => {
      return function (...args: unknown[]) {
        const globalAnalytics = window[globalAnalyticsKey];
        if (globalAnalytics?.initialized) {
          // Sometimes users assigned analytics to a variable before analytics is done loading, resulting in a stale reference.
          // If so, proxy any calls to the 'real' analytics instance.
          return (globalAnalytics[e] as (...callArgs: unknown[]) => unknown).apply(
            globalAnalytics,
            args
          );
        }

        // Add buffered page context object so page information is always up-to-date
        if (["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1) {
          const c = document.querySelector("link[rel='canonical']");
          args.push({
            __t: "bpc",
            c: (c && c.getAttribute("href")) || undefined,
            p: location.pathname,
            u: location.href,
            s: location.search,
            t: document.title,
            r: document.referrer,
          });
        }

        args.unshift(e);
        analytics.push(args);
        return analytics;
      };
    };
    analytics.factory = factory;

    // For each of our methods, generate a queueing stub.
    for (const key of methods) {
      analytics[key] = factory(key);
    }

    // Define a method to load Analytics.js from our CDN,
    // and that will be sure to only ever load it once.
    const load = (key: string, options?: unknown) => {
      // Create an async script element based on your key.
      const t = document.createElement("script");
      t.type = "text/javascript";
      t.async = true;
      t.setAttribute("data-global-segment-analytics-key", globalAnalyticsKey);
      t.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";

      // Insert our script next to the first script element.
      const first = document.getElementsByTagName("script")[0];
      if (first && first.parentNode) {
        first.parentNode.insertBefore(t, first);
      }
      analytics._loadOptions = options;
    };
    analytics.load = load;
    analytics._writeKey = segmentWriteKey;

    // Add a version to keep track of what's in the wild.
    analytics.SNIPPET_VERSION = "5.2.1";

    // Load Analytics.js with your key, which will automatically
    // load the tools you've enabled for your account. Boosh!
    load(segmentWriteKey);

    analyticsRef.current = analytics;
  }, [segmentWriteKey]);

  return analyticsRef.current;
};

export const useSegmentPage = (additionalProperties = {}) => {
  const segment = useSegment();
  const location = useLocation();

  useEffect(() => {
    if (segment && typeof segment.page === "function") {
      segment.page({
        ...additionalProperties,
        tenant: getCurrentSelectedTenant(),
      });
    }
  }, [location.pathname, segment, additionalProperties]);
};

export const useSegmentTrackEvent = () => {
  useSegment();

  const trackEvent = (eventName: string, payload: object) => {
    const segment = window.analytics;
    if (segment && typeof segment.track === "function") {
      segment.track(eventName, {
        ...payload,
        tenant: getCurrentSelectedTenant(),
      });
    }
  };

  return trackEvent;
};
